import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUploadZone = ({
  documentType,
  uploadedFile,
  onFileSelect,
  onFileRemove,
  acceptedFormats = '.pdf,.jpg,.jpeg,.png',
  maxSizeMB = 10,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const documentLabels = {
    aadhar: 'Aadhar Card',
    birthCertificate: 'Birth Certificate',
    passportPhoto: 'Passport Size Photo',
    schoolId: 'School ID Card',
    bonafide: 'Bonafide Certificate',
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileValidation(files?.[0]);
    }
  };

  const handleFileValidation = (file) => {
    const maxSize = maxSizeMB * 1024 * 1024;
    const allowedTypes = acceptedFormats?.split(',')?.map((f) => f?.trim());
    const fileExtension = '.' + file?.name?.split('.')?.pop()?.toLowerCase();

    if (file?.size > maxSize) {
      alert(`File size exceeds ${maxSizeMB}MB limit. Please select a smaller file.`);
      return;
    }

    if (!allowedTypes?.includes(fileExtension)) {
      alert(`Invalid file format. Accepted formats: ${acceptedFormats}`);
      return;
    }

    simulateUpload(file);
  };

  const simulateUpload = (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          // Pass both file metadata and the actual File object
          onFileSelect(documentType, {
            name: file?.name,
            size: file?.size,
            type: file?.type,
            uploadedAt: new Date()?.toISOString(),
            url: URL.createObjectURL(file),
            file: file // Pass the actual File object for upload
          });
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files?.length > 0) {
      handleFileValidation(files?.[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={18} className="text-primary" />
          {documentLabels?.[documentType]}
        </h3>
        {uploadedFile && (
          <span className="text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle2" size={14} />
            Uploaded
          </span>
        )}
      </div>
      {!uploadedFile && !isUploading && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef?.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 cursor-pointer ${
            isDragging
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
        >
          <Icon
            name="Upload"
            size={32}
            className="mx-auto mb-3 text-muted-foreground"
          />
          <p className="text-sm text-foreground mb-1">
            Drag & drop your file here
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            or click to browse
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef?.current?.click();
            }}
          >
            Select File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats}
            onChange={handleFileInputChange}
            className="hidden"
          />
          <p className="text-xs text-muted-foreground mt-3">
            Max size: {maxSizeMB}MB • Formats: {acceptedFormats}
          </p>
        </div>
      )}
      {isUploading && (
        <div className="py-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            <span className="text-sm text-foreground">Uploading...</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-right">
            {uploadProgress}%
          </p>
        </div>
      )}
      {uploadedFile && (
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="FileCheck" size={24} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {uploadedFile?.name}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatFileSize(uploadedFile?.size)} • Uploaded on{' '}
                {new Date(uploadedFile.uploadedAt)?.toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  variant="outline"
                  size="xs"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => window.open(uploadedFile?.url, '_blank')}
                >
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => fileInputRef?.current?.click()}
                >
                  Replace
                </Button>
                <Button
                  variant="destructive"
                  size="xs"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={() => onFileRemove(documentType)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

export default DocumentUploadZone;